import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  computeCanonicalTreeTopology,
  type CanonicalTreeTopology,
} from './CanonicalTreeTopologyAuthority';

import {
  projectCanonicalTreeCompatibilityEdges,
} from './CanonicalTreeCompatibilityEdgeProjection';

import {
  projectCanonicalTreeForViewer,
} from './CanonicalTreeViewerProjection';

import {
  resolveCanonicalTreeLayoutPathRuntime,
} from './CanonicalTreeLayoutPathRuntimeSelector';

export type CanonicalTreeTopologyReplacementReadiness =
  | 'SAFE'
  | 'BLOCKED';

export type CanonicalTreeTopologyReplacementClassification =
  | 'PARITY'
  | 'LEGACY-ONLY'
  | 'CANONICAL-ONLY'
  | 'GENERATION-MISMATCH'
  | 'BRANCH-PATH-MISMATCH'
  | 'UNRESOLVED';

export interface CanonicalTreeTopologyReplacementMemberResult {
  readonly memberId: string;

  readonly classification:
    CanonicalTreeTopologyReplacementClassification;

  readonly legacyGeneration:
    number | null;

  readonly candidateGeneration:
    number | null;

  readonly legacyBranchPath:
    string | null;

  readonly candidateBranchPath:
    string | null;

  readonly details:
    readonly string[];
}

export interface CanonicalTreeTopologyReplacementSummary {
  readonly parityCount: number;
  readonly legacyOnlyCount: number;
  readonly canonicalOnlyCount: number;
  readonly generationMismatchCount: number;
  readonly branchPathMismatchCount: number;
  readonly unresolvedCount: number;
}

export interface CanonicalTreeTopologyReplacementSafetyResult {
  readonly viewerMemberId:
    string | null;

  readonly legacyTopology:
    CanonicalTreeTopology;

  readonly candidateTopology:
    CanonicalTreeTopology;

  readonly selectedTopology:
    CanonicalTreeTopology;

  readonly selectedSource:
    | 'canonical-compatible'
    | 'legacy-fallback';

  readonly readiness:
    CanonicalTreeTopologyReplacementReadiness;

  readonly members:
    readonly CanonicalTreeTopologyReplacementMemberResult[];

  readonly summary:
    CanonicalTreeTopologyReplacementSummary;

  readonly compatibilityUnresolvedCount: number;
  readonly compatibilityInvalidCount: number;
  readonly canonicalUnresolvedCount: number;
  readonly canonicalConflictingCount: number;
  readonly layoutPathMismatchCount: number;
  readonly layoutPathLegacyOnlyCount: number;
}

export interface CanonicalTreeTopologyReplacementSafetyInput {
  readonly members:
    readonly GraphMember[];

  readonly viewerMemberId:
    string | null | undefined;
}

const freezeTopology = (
  topology:
    CanonicalTreeTopology,
): CanonicalTreeTopology =>
  Object.freeze({
    generationByMemberId:
      Object.freeze({
        ...topology.generationByMemberId,
      }),

    branchPathByMemberId:
      Object.freeze({
        ...topology.branchPathByMemberId,
      }),
  });

const countClassification = (
  members:
    readonly CanonicalTreeTopologyReplacementMemberResult[],

  classification:
    CanonicalTreeTopologyReplacementClassification,
): number =>
  members.filter(
    member =>
      member.classification ===
      classification,
  ).length;

const freezeMemberResult = (
  result:
    CanonicalTreeTopologyReplacementMemberResult,
): CanonicalTreeTopologyReplacementMemberResult =>
  Object.freeze({
    ...result,

    details:
      Object.freeze([
        ...result.details,
      ]),
  });

export const runCanonicalTreeTopologyReplacementSafetyGate = (
  input:
    CanonicalTreeTopologyReplacementSafetyInput,
): CanonicalTreeTopologyReplacementSafetyResult => {
  const members =
    [...input.members];

  const viewerMemberId =
    input.viewerMemberId ?? null;

  const legacyTopology =
    computeCanonicalTreeTopology(
      members,
      viewerMemberId,
    );

  if (
    !viewerMemberId ||
    !members.some(
      member =>
        member.id === viewerMemberId,
    )
  ) {
    const frozenLegacy =
      freezeTopology(
        legacyTopology,
      );

    return Object.freeze({
      viewerMemberId,

      legacyTopology:
        frozenLegacy,

      candidateTopology:
        freezeTopology({
          generationByMemberId: {},
          branchPathByMemberId: {},
        }),

      selectedTopology:
        frozenLegacy,

      selectedSource:
        'legacy-fallback',

      readiness:
        'BLOCKED',

      members:
        Object.freeze([]),

      summary:
        Object.freeze({
          parityCount: 0,
          legacyOnlyCount:
            Object.keys(
              legacyTopology
                .generationByMemberId,
            ).length,
          canonicalOnlyCount: 0,
          generationMismatchCount: 0,
          branchPathMismatchCount: 0,
          unresolvedCount: 1,
        }),

      compatibilityUnresolvedCount: 0,
      compatibilityInvalidCount: 0,
      canonicalUnresolvedCount: 1,
      canonicalConflictingCount: 0,
      layoutPathMismatchCount: 0,
      layoutPathLegacyOnlyCount: 0,
    });
  }

  const compatibility =
    projectCanonicalTreeCompatibilityEdges({
      members,
    });

  const canonical =
    projectCanonicalTreeForViewer({
      viewerId:
        viewerMemberId,

      members,

      edges:
        compatibility.edges,
    });

  const layoutPaths =
    resolveCanonicalTreeLayoutPathRuntime({
      members,
      viewerId:
        viewerMemberId,
    });

  const candidateTopology:
    CanonicalTreeTopology = {
      generationByMemberId: {},
      branchPathByMemberId: {},
    };

  for (
    const [
      memberId,
      projection,
    ] of canonical.projections
  ) {
    if (
      projection.generation !== null
    ) {
      candidateTopology
        .generationByMemberId[
          memberId
        ] =
        projection.generation;
    }
  }

  for (
    const [
      memberId,
      branchPath,
    ] of layoutPaths
      .branchPathByMemberId
  ) {
    candidateTopology
      .branchPathByMemberId[
        memberId
      ] =
      branchPath;
  }

  const allMemberIds =
    Array.from(
      new Set([
        ...members.map(
          member => member.id,
        ),

        ...Object.keys(
          legacyTopology
            .generationByMemberId,
        ),

        ...Object.keys(
          legacyTopology
            .branchPathByMemberId,
        ),

        ...Object.keys(
          candidateTopology
            .generationByMemberId,
        ),

        ...Object.keys(
          candidateTopology
            .branchPathByMemberId,
        ),
      ]),
    ).sort();

  const memberResults =
    allMemberIds.map(
      memberId => {
        const details:
          string[] = [];

        const legacyGeneration =
          legacyTopology
            .generationByMemberId[
              memberId
            ] ??
          null;

        const candidateGeneration =
          candidateTopology
            .generationByMemberId[
              memberId
            ] ??
          null;

        const legacyBranchPath =
          legacyTopology
            .branchPathByMemberId[
              memberId
            ] ??
          null;

        const candidateBranchPath =
          candidateTopology
            .branchPathByMemberId[
              memberId
            ] ??
          null;

        const canonicalUnresolved =
          canonical
            .unresolvedEvidence
            .some(
              evidence =>
                evidence.memberIds
                  .includes(
                    memberId,
                  ),
            );

        if (canonicalUnresolved) {
          details.push(
            'Canonical viewer projection reported unresolved evidence.',
          );

          return freezeMemberResult({
            memberId,
            classification:
              'UNRESOLVED',
            legacyGeneration,
            candidateGeneration,
            legacyBranchPath,
            candidateBranchPath,
            details,
          });
        }

        const legacyReachable =
          legacyGeneration !== null ||
          legacyBranchPath !== null;

        const candidateReachable =
          candidateGeneration !== null ||
          candidateBranchPath !== null;

        if (
          legacyReachable &&
          !candidateReachable
        ) {
          details.push(
            'Member exists only in the legacy mutation topology.',
          );

          return freezeMemberResult({
            memberId,
            classification:
              'LEGACY-ONLY',
            legacyGeneration,
            candidateGeneration,
            legacyBranchPath,
            candidateBranchPath,
            details,
          });
        }

        if (
          !legacyReachable &&
          candidateReachable
        ) {
          details.push(
            'Member exists only in the canonical candidate topology.',
          );

          return freezeMemberResult({
            memberId,
            classification:
              'CANONICAL-ONLY',
            legacyGeneration,
            candidateGeneration,
            legacyBranchPath,
            candidateBranchPath,
            details,
          });
        }

        if (
          legacyGeneration !==
          candidateGeneration
        ) {
          details.push(
            `Generation differs: ${legacyGeneration ?? 'missing'} -> ${candidateGeneration ?? 'missing'}.`,
          );

          return freezeMemberResult({
            memberId,
            classification:
              'GENERATION-MISMATCH',
            legacyGeneration,
            candidateGeneration,
            legacyBranchPath,
            candidateBranchPath,
            details,
          });
        }

        if (
          legacyBranchPath !==
          candidateBranchPath
        ) {
          details.push(
            `Numeric branch path differs: ${legacyBranchPath ?? 'missing'} -> ${candidateBranchPath ?? 'missing'}.`,
          );

          return freezeMemberResult({
            memberId,
            classification:
              'BRANCH-PATH-MISMATCH',
            legacyGeneration,
            candidateGeneration,
            legacyBranchPath,
            candidateBranchPath,
            details,
          });
        }

        details.push(
          'Legacy mutation topology and canonical-compatible candidate agree.',
        );

        return freezeMemberResult({
          memberId,
          classification:
            'PARITY',
          legacyGeneration,
          candidateGeneration,
          legacyBranchPath,
          candidateBranchPath,
          details,
        });
      },
    );

  const summary:
    CanonicalTreeTopologyReplacementSummary =
      Object.freeze({
        parityCount:
          countClassification(
            memberResults,
            'PARITY',
          ),

        legacyOnlyCount:
          countClassification(
            memberResults,
            'LEGACY-ONLY',
          ),

        canonicalOnlyCount:
          countClassification(
            memberResults,
            'CANONICAL-ONLY',
          ),

        generationMismatchCount:
          countClassification(
            memberResults,
            'GENERATION-MISMATCH',
          ),

        branchPathMismatchCount:
          countClassification(
            memberResults,
            'BRANCH-PATH-MISMATCH',
          ),

        unresolvedCount:
          countClassification(
            memberResults,
            'UNRESOLVED',
          ),
      });

  const blocked =
    compatibility
      .unresolvedEvidence.length > 0 ||
    compatibility
      .invalidEvidence.length > 0 ||
    canonical
      .unresolvedEvidence.length > 0 ||
    canonical
      .conflictingEvidence.length > 0 ||
    layoutPaths.readiness ===
      'BLOCKED' ||
    layoutPaths
      .mismatchCount > 0 ||
    layoutPaths
      .legacyOnlyCount > 0 ||
    summary
      .legacyOnlyCount > 0 ||
    summary
      .canonicalOnlyCount > 0 ||
    summary
      .generationMismatchCount > 0 ||
    summary
      .branchPathMismatchCount > 0 ||
    summary
      .unresolvedCount > 0;

  const readiness:
    CanonicalTreeTopologyReplacementReadiness =
      blocked
        ? 'BLOCKED'
        : 'SAFE';

  const frozenLegacy =
    freezeTopology(
      legacyTopology,
    );

  const frozenCandidate =
    freezeTopology(
      candidateTopology,
    );

  return Object.freeze({
    viewerMemberId,

    legacyTopology:
      frozenLegacy,

    candidateTopology:
      frozenCandidate,

    selectedTopology:
      readiness === 'SAFE'
        ? frozenCandidate
        : frozenLegacy,

    selectedSource:
      readiness === 'SAFE'
        ? 'canonical-compatible'
        : 'legacy-fallback',

    readiness,

    members:
      Object.freeze(
        memberResults,
      ),

    summary,

    compatibilityUnresolvedCount:
      compatibility
        .unresolvedEvidence.length,

    compatibilityInvalidCount:
      compatibility
        .invalidEvidence.length,

    canonicalUnresolvedCount:
      canonical
        .unresolvedEvidence.length,

    canonicalConflictingCount:
      canonical
        .conflictingEvidence.length,

    layoutPathMismatchCount:
      layoutPaths
        .mismatchCount,

    layoutPathLegacyOnlyCount:
      layoutPaths
        .legacyOnlyCount,
  });
};
